import { useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  Camera,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Loader2,
  RefreshCw,
  Search,
  X,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useAuth } from '@/features/auth';
import { cn } from '@/lib/utils';
import { type Language } from '@/types';
import {
  type ApprovalJob,
  approveJob,
  loadPendingApprovalJobs,
} from '../api';

interface ApprovalWorkspaceProps {
  language: Language;
}

type DateBucket = 'today' | 'yesterday' | 'thisWeek' | 'older';

const BUCKET_ORDER: DateBucket[] = ['today', 'yesterday', 'thisWeek', 'older'];

const copy = {
  sv: {
    title: 'Godkänn jobb',
    subtitle: 'Granska och godkänn slutförda städningar.',
    refresh: 'Uppdatera',
    loading: 'Laddar…',
    empty: 'Inga jobb väntar godkännande.',
    today: 'Idag',
    yesterday: 'Igår',
    thisWeek: 'Denna vecka',
    older: 'Äldre',
    pendingTab: 'Väntar',
    approvedTab: 'Godkända',
    searchPlaceholder: 'Sök BRF, städare…',
    cleanerFilter: 'Städare',
    siteFilter: 'BRF',
    allCleaners: 'Alla städare',
    allSites: 'Alla BRF',
    approveSelected: 'Godkänn markerade',
    approving: 'Godkänner…',
    approve: 'Godkänn',
    selectAll: 'Markera alla',
    blocks: 'block',
    points: 'p',
    skipped: 'överhoppade',
    notes: 'noter',
    photos: 'bild',
    stale: 'Äldre än 7 dagar',
    staleDays: (n: number) => `Slutförd för ${n} dagar sedan — väntar fortfarande på godkännande.`,
    monthFilter: 'Månad',
    allMonths: 'Alla månader',
    nothingHere: 'Inget i denna kategori.',
    cleanerJobsCount: (n: number) => `${n} jobb`,
  },
  en: {
    title: 'Approve jobs',
    subtitle: 'Review and approve completed cleanings.',
    refresh: 'Refresh',
    loading: 'Loading…',
    empty: 'No jobs pending approval.',
    today: 'Today',
    yesterday: 'Yesterday',
    thisWeek: 'This week',
    older: 'Older',
    pendingTab: 'Pending',
    approvedTab: 'Approved',
    searchPlaceholder: 'Search BRF, cleaner…',
    cleanerFilter: 'Cleaner',
    siteFilter: 'BRF',
    allCleaners: 'All cleaners',
    allSites: 'All BRFs',
    approveSelected: 'Approve selected',
    approving: 'Approving…',
    approve: 'Approve',
    selectAll: 'Select all',
    blocks: 'blocks',
    points: 'pts',
    skipped: 'skipped',
    notes: 'notes',
    photos: 'photo',
    stale: 'Older than 7 days',
    staleDays: (n: number) => `Completed ${n} days ago — still awaiting approval.`,
    monthFilter: 'Month',
    allMonths: 'All months',
    nothingHere: 'Nothing in this bucket.',
    cleanerJobsCount: (n: number) => `${n} jobs`,
  },
} as const;

const ALL = '__all__';

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function yesterdayIso(): string {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
}

function startOfWeekIso(): string {
  const d = new Date();
  const day = d.getDay() || 7;
  d.setDate(d.getDate() - (day - 1));
  return d.toISOString().slice(0, 10);
}

function bucketFor(scheduledFor: string): DateBucket {
  const today = todayIso();
  const yesterday = yesterdayIso();
  const weekStart = startOfWeekIso();
  if (scheduledFor === today) return 'today';
  if (scheduledFor === yesterday) return 'yesterday';
  if (scheduledFor >= weekStart) return 'thisWeek';
  return 'older';
}

function isStale(scheduledFor: string): boolean {
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - 7);
  return new Date(scheduledFor) < cutoff;
}

function daysSince(scheduledFor: string): number {
  const ms = Date.now() - new Date(scheduledFor).getTime();
  return Math.max(0, Math.floor(ms / (1000 * 60 * 60 * 24)));
}

function monthKeyOf(scheduledFor: string): string {
  return scheduledFor.slice(0, 7);
}

function monthLabel(yyyymm: string, language: Language): string {
  const [y, m] = yyyymm.split('-');
  const monthIdx = Number.parseInt(m, 10) - 1;
  const date = new Date(Date.UTC(Number.parseInt(y, 10), monthIdx, 1));
  return date.toLocaleDateString(language === 'sv' ? 'sv-SE' : 'en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

type JobCardProps = {
  job: ApprovalJob;
  language: Language;
  selected: boolean;
  onToggleSelect: () => void;
  onApprove: () => void;
  isApproving: boolean;
  showCheckbox: boolean;
};

function JobCard({ job, language, selected, onToggleSelect, onApprove, isApproving, showCheckbox }: JobCardProps) {
  const [expanded, setExpanded] = useState(false);
  const text = copy[language];
  const completedBlocks = job.blocks.filter((b) => b.status === 'completed').length;
  const skippedBlocks = job.blocks.filter((b) => b.status === 'skipped').length;
  const blocksWithNotes = job.blocks.filter((b) => b.completion_notes).length;
  const stale = isStale(job.scheduled_for);
  const isApproved = job.status === 'approved';

  return (
    <Card className={`overflow-hidden ${selected ? 'ring-2 ring-primary' : ''}`}>
      <div className="flex items-start gap-2 p-3">
        {showCheckbox && !isApproved && (
          <Checkbox
            checked={selected}
            onCheckedChange={onToggleSelect}
            className="mt-1"
            aria-label="Markera"
          />
        )}
        <button
          type="button"
          className="flex-1 min-w-0 text-left space-y-1"
          onClick={() => setExpanded((v) => !v)}
        >
          <div className="flex items-start justify-between gap-2">
            <div className="font-medium truncate">{job.site_name}</div>
            <div className="flex items-center gap-1 shrink-0">
              {stale && !isApproved && (
                <TooltipProvider delayDuration={150}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Badge
                        variant="destructive"
                        className="text-[10px] px-1 cursor-help"
                        onClick={(event) => event.stopPropagation()}
                      >
                        !
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent side="left">
                      {text.staleDays(daysSince(job.scheduled_for))}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              {expanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
            </div>
          </div>
          <div className="text-xs text-muted-foreground truncate">
            {job.assigned_user_name} · {job.scheduled_for}
          </div>
          <div className="flex flex-wrap items-center gap-1.5 pt-1">
            <Badge variant="outline" className="text-[10px]">
              {completedBlocks}/{job.blocks.length} {text.blocks}
            </Badge>
            {job.total_points > 0 && (
              <Badge variant="secondary" className="text-[10px]">
                {job.total_points} {text.points}
              </Badge>
            )}
            {skippedBlocks > 0 && (
              <Badge variant="warning" className="text-[10px] gap-1">
                <AlertCircle className="h-3 w-3" />
                {skippedBlocks} {text.skipped}
              </Badge>
            )}
            {blocksWithNotes > 0 && (
              <Badge variant="info" className="text-[10px]">
                {blocksWithNotes} {text.notes}
              </Badge>
            )}
          </div>
        </button>
      </div>

      {expanded && (
        <div className="space-y-2 border-t px-3 pb-3 pt-2 bg-muted/30">
          <div className="space-y-1.5">
            {job.blocks.map((block) => (
              <div key={block.id} className="flex items-start gap-2 text-sm">
                {block.status === 'completed' && <Check className="h-3.5 w-3.5 text-emerald-600 mt-0.5 shrink-0" />}
                {block.status === 'skipped' && <AlertCircle className="h-3.5 w-3.5 text-amber-600 mt-0.5 shrink-0" />}
                {block.status === 'cancelled' && <X className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className={block.status === 'cancelled' ? 'line-through text-muted-foreground' : ''}>
                    <span className="font-medium">{block.source_code}</span>
                    <span className="text-muted-foreground"> · {block.area}</span>
                  </div>
                  {block.completion_notes && (
                    <div className="text-xs text-muted-foreground italic mt-0.5">"{block.completion_notes}"</div>
                  )}
                </div>
              </div>
            ))}
          </div>
          {!isApproved && (
            <div className="pt-1">
              <Button type="button" size="sm" onClick={onApprove} disabled={isApproving}>
                {isApproving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                {text.approve}
              </Button>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

type ColumnProps = {
  title: string;
  jobs: ApprovalJob[];
  language: Language;
  selectedIds: Set<string>;
  onToggleJob: (id: string) => void;
  onSelectAllInColumn: () => void;
  onApproveOne: (id: string) => void;
  approvingJobId: string | null;
  emptyText: string;
  cleanerCopy: (n: number) => string;
  collapsed: boolean;
  onToggleCollapsed: () => void;
};

function Column({ title, jobs, language, selectedIds, onToggleJob, onSelectAllInColumn, onApproveOne, approvingJobId, emptyText, cleanerCopy, collapsed, onToggleCollapsed }: ColumnProps) {
  const grouped = useMemo(() => {
    const map = new Map<string, ApprovalJob[]>();
    for (const job of jobs) {
      const key = job.assigned_user_name;
      const list = map.get(key) ?? [];
      list.push(job);
      map.set(key, list);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [jobs]);

  return (
    <div
      className={cn(
        'group/col relative flex flex-col rounded-lg border bg-card min-h-[200px] overflow-hidden',
        'transition-[max-width,min-width,flex-grow] duration-300 ease-in-out',
        'basis-0 flex-shrink',
        collapsed
          ? 'max-w-[2.5rem] min-w-[2.5rem] flex-grow-0 cursor-pointer hover:bg-accent'
          : 'max-w-[100%] min-w-[240px] flex-grow',
      )}
      onClick={collapsed ? onToggleCollapsed : undefined}
      role={collapsed ? 'button' : undefined}
      tabIndex={collapsed ? 0 : undefined}
      onKeyDown={(event) => {
        if (collapsed && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault();
          onToggleCollapsed();
        }
      }}
      title={collapsed ? `${title} (${jobs.length})` : undefined}
    >
      {/* Collapsed-rail layer */}
      <div
        className={cn(
          'absolute inset-0 flex flex-col items-center justify-between gap-3 py-3 pointer-events-none',
          'transition-opacity duration-300 ease-in-out',
          collapsed ? 'opacity-100 delay-150' : 'opacity-0 delay-0',
        )}
      >
        <ChevronsRight className="h-4 w-4 text-muted-foreground group-hover/col:text-foreground transition-colors" />
        <div className="flex flex-1 flex-col items-center justify-center gap-2">
          <span
            className="text-xs font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap"
            style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
          >
            {title}
          </span>
          <Badge variant={jobs.length === 0 ? 'outline' : 'secondary'} className="text-[10px]">
            {jobs.length}
          </Badge>
        </div>
        <div className="h-4 w-4" />
      </div>

      {/* Expanded content layer */}
      <div
        className={cn(
          'flex flex-col h-full min-w-[260px]',
          'transition-opacity duration-200 ease-in-out',
          collapsed ? 'opacity-0 pointer-events-none delay-0' : 'opacity-100 delay-150',
        )}
        aria-hidden={collapsed}
      >
        <div className="flex items-center justify-between gap-2 border-b px-3 py-2">
          <div className="flex items-center gap-2 min-w-0">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-6 w-6 shrink-0 transition-transform hover:-translate-x-0.5"
              onClick={(event) => {
                event.stopPropagation();
                onToggleCollapsed();
              }}
              title={language === 'sv' ? 'Minimera' : 'Collapse'}
            >
              <ChevronsLeft className="h-3.5 w-3.5" />
            </Button>
            <h3 className="font-semibold text-sm truncate">{title}</h3>
            <Badge variant="secondary" className="shrink-0">{jobs.length}</Badge>
          </div>
          {jobs.length > 0 && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs shrink-0"
              onClick={(event) => {
                event.stopPropagation();
                onSelectAllInColumn();
              }}
            >
              ☐ Alla
            </Button>
          )}
        </div>
        <div className="flex-1 space-y-3 p-2 overflow-y-auto">
          {jobs.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-6">{emptyText}</p>
          ) : (
            grouped.map(([cleanerName, cleanerJobs]) => (
              <div key={cleanerName} className="space-y-1.5">
                <div className="flex items-center gap-2 px-1 text-xs uppercase tracking-wide text-muted-foreground">
                  <span className="font-medium truncate">{cleanerName}</span>
                  <span className="shrink-0">{cleanerCopy(cleanerJobs.length)}</span>
                </div>
                <div className="space-y-1.5">
                  {cleanerJobs.map((job) => (
                    <JobCard
                      key={job.id}
                      job={job}
                      language={language}
                      selected={selectedIds.has(job.id)}
                      onToggleSelect={() => onToggleJob(job.id)}
                      onApprove={() => onApproveOne(job.id)}
                      isApproving={approvingJobId === job.id}
                      showCheckbox
                    />
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export function ApprovalWorkspace({ language }: ApprovalWorkspaceProps) {
  const { session } = useAuth();
  const text = copy[language];
  const [jobs, setJobs] = useState<ApprovalJob[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [approvingJobId, setApprovingJobId] = useState<string | null>(null);
  const [bulkApproving, setBulkApproving] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [tab, setTab] = useState<'pending' | 'approved'>('pending');
  const [search, setSearch] = useState('');
  const [cleanerFilter, setCleanerFilter] = useState<string>(ALL);
  const [siteFilter, setSiteFilter] = useState<string>(ALL);
  const [monthFilter, setMonthFilter] = useState<string>(ALL);
  const [manualCollapsed, setManualCollapsed] = useState<Record<DateBucket, boolean | null>>(() => {
    if (typeof window === 'undefined') return { today: null, yesterday: null, thisWeek: null, older: null };
    try {
      const raw = window.localStorage.getItem('approvals.manualCollapsed.v1');
      if (raw) return JSON.parse(raw) as Record<DateBucket, boolean | null>;
    } catch {
      // ignore
    }
    return { today: null, yesterday: null, thisWeek: null, older: null };
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem('approvals.manualCollapsed.v1', JSON.stringify(manualCollapsed));
    } catch {
      // ignore
    }
  }, [manualCollapsed]);

  const toggleColumnCollapsed = (bucket: DateBucket, currentCollapsed: boolean) =>
    setManualCollapsed((prev) => ({ ...prev, [bucket]: !currentCollapsed }));

  const sessionToken = session?.access_token ?? null;

  const refresh = async (mode: 'load' | 'refresh' = 'refresh') => {
    if (mode === 'load') setIsLoading(true);
    try {
      const next = await loadPendingApprovalJobs(sessionToken);
      setJobs(next);
    } catch (error) {
      console.error('Failed to load approval jobs:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void refresh('load');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionToken]);

  const visible = useMemo(() => {
    return jobs.filter((j) => {
      if (tab === 'pending' && j.status !== 'completed') return false;
      if (tab === 'approved' && j.status !== 'approved') return false;
      if (cleanerFilter !== ALL && j.assigned_user_id !== cleanerFilter) return false;
      if (siteFilter !== ALL && j.site_id !== siteFilter) return false;
      if (monthFilter !== ALL && monthKeyOf(j.scheduled_for) !== monthFilter) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        if (!j.site_name.toLowerCase().includes(q) && !j.assigned_user_name.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [jobs, tab, cleanerFilter, siteFilter, monthFilter, search]);

  const byBucket = useMemo(() => {
    const map: Record<DateBucket, ApprovalJob[]> = { today: [], yesterday: [], thisWeek: [], older: [] };
    for (const job of visible) {
      map[bucketFor(job.scheduled_for)].push(job);
    }
    return map;
  }, [visible]);

  const cleanerOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const job of jobs) map.set(job.assigned_user_id, job.assigned_user_name);
    return [...map.entries()].sort(([, a], [, b]) => a.localeCompare(b));
  }, [jobs]);

  const siteOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const job of jobs) map.set(job.site_id, job.site_name);
    return [...map.entries()].sort(([, a], [, b]) => a.localeCompare(b));
  }, [jobs]);

  const monthOptions = useMemo(() => {
    const set = new Set<string>();
    for (const job of jobs) set.add(monthKeyOf(job.scheduled_for));
    return [...set].sort().reverse();
  }, [jobs]);

  const pendingCount = jobs.filter((j) => j.status === 'completed').length;
  const approvedCount = jobs.filter((j) => j.status === 'approved').length;

  const toggleJob = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const selectAllInBucket = (bucket: DateBucket) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const job of byBucket[bucket]) {
        if (job.status === 'completed') next.add(job.id);
      }
      return next;
    });

  const clearSelection = () => setSelectedIds(new Set());

  const approveOne = async (jobId: string) => {
    setApprovingJobId(jobId);
    try {
      await approveJob(jobId, sessionToken);
      await refresh();
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(jobId);
        return next;
      });
    } catch (error) {
      console.error('Failed to approve job:', error);
    } finally {
      setApprovingJobId(null);
    }
  };

  const approveSelected = async () => {
    if (selectedIds.size === 0) return;
    setBulkApproving(true);
    try {
      for (const id of selectedIds) {
        try {
          await approveJob(id, sessionToken);
        } catch (error) {
          console.error('Bulk approval failed for', id, error);
        }
      }
      await refresh();
      clearSelection();
    } finally {
      setBulkApproving(false);
    }
  };

  return (
    <div className="space-y-4 pb-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-0.5">
          <h1 className="text-2xl font-semibold">{text.title}</h1>
          <p className="text-sm text-muted-foreground">{text.subtitle}</p>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={() => void refresh()} disabled={isLoading}>
          <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          {text.refresh}
        </Button>
      </header>

      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-md border bg-card p-0.5">
          <Button
            type="button"
            variant={tab === 'pending' ? 'secondary' : 'ghost'}
            size="sm"
            className="h-8"
            onClick={() => setTab('pending')}
          >
            {text.pendingTab} <Badge variant="outline" className="ml-1.5">{pendingCount}</Badge>
          </Button>
          <Button
            type="button"
            variant={tab === 'approved' ? 'secondary' : 'ghost'}
            size="sm"
            className="h-8"
            onClick={() => setTab('approved')}
          >
            {text.approvedTab} <Badge variant="outline" className="ml-1.5">{approvedCount}</Badge>
          </Button>
        </div>

        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={text.searchPlaceholder}
            className="pl-8 h-9"
          />
        </div>

        <Select value={cleanerFilter} onValueChange={setCleanerFilter}>
          <SelectTrigger className="h-9 w-[180px]">
            <SelectValue placeholder={text.cleanerFilter} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{text.allCleaners}</SelectItem>
            {cleanerOptions.map(([id, name]) => (
              <SelectItem key={id} value={id}>{name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={siteFilter} onValueChange={setSiteFilter}>
          <SelectTrigger className="h-9 w-[200px]">
            <SelectValue placeholder={text.siteFilter} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{text.allSites}</SelectItem>
            {siteOptions.map(([id, name]) => (
              <SelectItem key={id} value={id}>{name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={monthFilter} onValueChange={setMonthFilter}>
          <SelectTrigger className="h-9 w-[170px]">
            <SelectValue placeholder={text.monthFilter} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{text.allMonths}</SelectItem>
            {monthOptions.map((key) => (
              <SelectItem key={key} value={key}>{monthLabel(key, language)}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {selectedIds.size > 0 && (
          <div className="flex items-center gap-2 ml-auto">
            <span className="text-sm text-muted-foreground">{selectedIds.size} markerade</span>
            <Button type="button" variant="ghost" size="sm" onClick={clearSelection}>
              <X className="h-4 w-4" />
            </Button>
            <Button type="button" size="sm" onClick={() => void approveSelected()} disabled={bulkApproving}>
              {bulkApproving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              {bulkApproving ? text.approving : `${text.approveSelected} (${selectedIds.size})`}
            </Button>
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : visible.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            {text.empty}
          </CardContent>
        </Card>
      ) : (
        <div className="flex flex-col gap-3 lg:flex-row lg:items-stretch">
          {BUCKET_ORDER.map((bucket) => {
            const manual = manualCollapsed[bucket];
            const isEmpty = byBucket[bucket].length === 0;
            const collapsed = manual === null ? isEmpty : manual;
            return (
              <Column
                key={bucket}
                title={text[bucket]}
                jobs={byBucket[bucket]}
                language={language}
                selectedIds={selectedIds}
                onToggleJob={toggleJob}
                onSelectAllInColumn={() => selectAllInBucket(bucket)}
                onApproveOne={(id) => void approveOne(id)}
                approvingJobId={approvingJobId}
                emptyText={text.nothingHere}
                cleanerCopy={text.cleanerJobsCount}
                collapsed={collapsed}
                onToggleCollapsed={() => toggleColumnCollapsed(bucket, collapsed)}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
